import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import LocalStorageService, { LocalStorageKeys } from "../services/localStorageService";
import { logoutSuccess } from "../redux/slices/auth/slice";
import { refreshAccessToken } from "../redux/slices/auth/actions";
import { AppDispatch } from "../redux/store";

// Extend AxiosRequestConfig to include _retry
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Queue item type
interface FailedQueueItem {
  resolve: (token: string) => void;
  reject: (error: AxiosError) => void;
}

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

// ================= REQUEST INTERCEPTOR =================
api.interceptors.request.use((config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = LocalStorageService.get<string>(LocalStorageKeys.ACCESS_TOKEN);
    const isAuthApi = config.url?.startsWith("/api/auth") || config.url?.startsWith("/auth");
    //  Add Authorization header only if it's not an auth API
    if (token && !isAuthApi) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Add language header globally
    config.headers.language = "en";
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// ================= REFRESH CONTROL VARIABLES =================
let isRefreshing = false;
let failedQueue: FailedQueueItem[] = [];

// ================= PROCESS QUEUE FUNCTION =================
const processQueue = (
  error: AxiosError | null,
  token: string | null = null
): void => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else if (token) {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

// ================= RESPONSE INTERCEPTOR =================
export const setupInterceptors = (dispatch: AppDispatch): void => {
  
  api.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => response,
    
    async (error: AxiosError): Promise<any> => {
      const originalRequest = error.config as CustomAxiosRequestConfig;
      if (!error.response) {
        return Promise.reject(error);
      }
      const status = error.response.status;
      
      const isTokenExpired =
        status === 401 ||
        status === 497 ||
        status === 498;

      if (isTokenExpired && !originalRequest._retry) {
        // VERY IMPORTANT: Skip refresh API itself
        if (originalRequest.url?.includes("/auth/refresh-token")) {
          dispatch(logoutSuccess());
          window.location.replace("/login");
          return Promise.reject(error);
        }
        originalRequest._retry = true;
        const refreshToken = LocalStorageService.get<string>( LocalStorageKeys.REFRESH_TOKEN );

        if (!refreshToken) {
          dispatch(logoutSuccess());
          return Promise.reject(error);
        }

        // CASE 1: Already refreshing
        if (isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          });
        }

        // CASE 2: Start refresh
        isRefreshing = true;
        try {          
          const newAccessToken = await dispatch( refreshAccessToken({ refreshToken })).unwrap();          
          processQueue(null, newAccessToken);
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        }
        catch (refreshError) {          
          processQueue(refreshError as AxiosError, null);
          dispatch(logoutSuccess());
          window.location.replace("/login");
          return Promise.reject(refreshError);
        }
        finally {
          isRefreshing = false;
        }
      }
      return Promise.reject(error);
    }
  );
};

export default api;
